import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Field, Label, Select, Textarea, Input, Description } from '@headlessui/react';
import { Stack } from '@mui/material';
import { clsx } from 'clsx';
import { useRef, useCallback, useEffect } from 'react';
import { useFormState, useFormContext, useForm } from 'react-hook-form';
import { Icon } from '../Icon/Icon.js';
import '@react-icons/all-files/bs/BsLayersFill.js';
import '@react-icons/all-files/bs/BsStars.js';
import '@react-icons/all-files/cg/CgSidebarOpen.js';
import '@react-icons/all-files/cg/CgSidebarRight.js';
import '@react-icons/all-files/fa/FaChevronDown.js';
import '@react-icons/all-files/fa/FaRedditSquare.js';
import '@react-icons/all-files/fa/FaSalesforce.js';
import '@react-icons/all-files/fa/FaSlack.js';
import '@react-icons/all-files/fa/FaTelegram.js';
import '@react-icons/all-files/fa6/FaSquareXTwitter.js';
import '@react-icons/all-files/md/MdAdd.js';
import '@react-icons/all-files/md/MdCheck.js';
import '@react-icons/all-files/md/MdClose.js';
import '@react-icons/all-files/md/MdEmail.js';
import '@react-icons/all-files/md/MdErrorOutline.js';
import '@react-icons/all-files/md/MdHelpOutline.js';
import '@react-icons/all-files/md/MdInfoOutline.js';
import '@react-icons/all-files/md/MdLanguage.js';
import '@react-icons/all-files/md/MdLink.js';
import '@react-icons/all-files/md/MdMoreVert.js';
import '@react-icons/all-files/md/MdOutlineThumbDown.js';
import '@react-icons/all-files/md/MdOutlineThumbUp.js';
import '@react-icons/all-files/md/MdOutlineWarningAmber.js';
import '@react-icons/all-files/md/MdPerson.js';
import '@react-icons/all-files/md/MdPhone.js';
import '@react-icons/all-files/md/MdShare.js';
import '@react-icons/all-files/rx/RxExternalLink.js';
import { Text } from '../Text/Text.js';
import { useName } from '../utils/useName.js';
import styles from './InputBase.module.css.js';

function InputBase({ name: providedName, label, placeholder, description, rules, value, defaultValue, maxLength, disabled, onChange, type = "text", autoComplete = "off", textFormatter, children, }) {
    var _a, _b, _c;
    const name = useName(providedName);
    const { register, setValue, formState: { errors }, control, } = useFormField();
    const touchedFields = useFormState({ name, control }).touchedFields;
    const previousValue = useRef(value);
    const handleChange = useCallback((event) => {
        if (value !== event.target.value) {
            const formattedValue = textFormatter ?
                textFormatter(event.target.value)
                : event.target.value;
            if (formattedValue !== event.target.value) {
                setValue(name, formattedValue, touchedFields[name] ? { shouldValidate: true } : undefined);
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(formattedValue);
        }
    }, [name, onChange, setValue, value, textFormatter, touchedFields]);
    useEffect(() => {
        if (value !== undefined && value !== previousValue.current) {
            previousValue.current = value;
            setValue(name, value);
        }
    }, [name, setValue, value]);
    const error = errors[name];
    return (jsxs(Field, { disabled: disabled, children: [label ?
                jsxs(Label, { className: styles.Label, passive: true, children: [jsx(Text, { prominence: "secondary", children: label }), (rules === null || rules === void 0 ? void 0 : rules.required) ?
                            jsx(Text, { accent: "error", children: " *" })
                            : null] })
                : null, type === "select" && (jsx(Select, Object.assign({}, register(name, Object.assign(Object.assign({}, rules), { onChange: handleChange })), { className: clsx(styles.Input, {
                    [styles.error]: Boolean(error),
                }), name: name, defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : value, children: children }))), type === "textarea" && (jsx(Textarea, Object.assign({}, register(name, Object.assign(Object.assign({}, rules), { onChange: handleChange })), { className: clsx(styles.Input, {
                    [styles.error]: Boolean(error),
                }), name: name, defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : value, placeholder: placeholder, maxLength: maxLength }))), type !== "select" && type !== "textarea" && (jsx(Input, Object.assign({}, register(name, Object.assign(Object.assign({}, rules), { onChange: handleChange })), { className: clsx(styles.Input, {
                    [styles.error]: Boolean(error),
                }), name: name, defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : value, type: type, placeholder: placeholder, maxLength: maxLength, autoComplete: autoComplete }))), ((_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : description) ?
                jsx(Description, { className: clsx(styles.Description, {
                        [styles.error]: Boolean(error === null || error === void 0 ? void 0 : error.message),
                    }), as: "div", children: jsx(Stack, { direction: "row", alignItems: "center", gap: "var(--space-smaller)", children: (error === null || error === void 0 ? void 0 : error.message) ?
                            jsxs(Fragment, { children: [jsx(Icon, { name: "error", size: "small" }), jsx("div", { style: { marginTop: "3px" }, children: jsx(Text, { prominence: "secondary", accent: "error", children: (_c = (_b = errors[name]) === null || _b === void 0 ? void 0 : _b.message) === null || _c === void 0 ? void 0 : _c.toString() }) })] })
                            : jsx("div", { style: { marginTop: "3px" }, children: jsx(Text, { prominence: "tertiary", accent: "none", children: description }) }) }) })
                : null] }));
}
function useFormField() {
    const formContext = useFormContext();
    const formMethods = useForm({ mode: "onTouched" });
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Typings are incorrect for 'useFormContext', it returns null when not wrapped in a FormProvider
    if (formContext) {
        return Object.assign({}, formContext);
    }
    return Object.assign({}, formMethods);
}

export { InputBase, useFormField };
//# sourceMappingURL=InputBase.js.map
